import { SegmentTypeEnum } from "./enums";
import { IArc, ICircle, ILine, IPoint, ISegment } from "./interfaces";

export type Vector = IPoint;

export class VectorHelpers {

    public static GetVectorLength(vector: Vector): number {
        return Math.sqrt(vector.x * vector.x + vector.y * vector.y);
    }

    public static NormalizeVector(vector: Vector): Vector {
        const length = VectorHelpers.GetVectorLength(vector);
        return { x: vector.x / length, y: vector.y / length };
    }

    public static Add(vector1: Vector, vector2: Vector): Vector {
        return { x: vector1.x + vector2.x, y: vector1.y + vector2.y };
    }

    public static Subtract(vector1: Vector, vector2: Vector): Vector {
        return { x: vector1.x - vector2.x, y: vector1.y - vector2.y };
    }

    public static Scale(vector: Vector, scale: number): Vector {
        return { x: vector.x * scale, y: vector.y * scale };
    }

    public static FromPoints(startPoint: IPoint, endPoint: IPoint): Vector {
        return { x: endPoint.x - startPoint.x, y: endPoint.y - startPoint.y } as Vector;
    }

    public static FromLine(line: ILine): Vector {
        return VectorHelpers.FromPoints(line.startPoint, line.endPoint);
    }

    public static GetPerpendicularVector(vector: Vector): Vector {
        return { x: -vector.y, y: vector.x };
    }

    public static FromAngle(angle: number): Vector {
        return { x: Math.cos(angle), y: Math.sin(angle) };
    }

    public static GetAngleFromVector(vector: Vector): number {
        return Math.atan2(vector.y, vector.x);
    }

    public static GetAngleBetweenVectors(vector1: Vector, vector2: Vector): number {
        return Math.acos(VectorHelpers.DotProduct(vector1, vector2) / (VectorHelpers.GetVectorLength(vector1) * VectorHelpers.GetVectorLength(vector2)));
    }

    public static DotProduct(vector1: Vector, vector2: Vector): number {
        return vector1.x * vector2.x + vector1.y * vector2.y;
    }

    public static CrossProduct(vector1: Vector, vector2: Vector): number {
        return vector1.x * vector2.y - vector1.y * vector2.x;
    }

}

export { VectorHelpers as vec }

export class GeometryHelpers {

    public static CalculateLineLineIntersection(line1: ILine, line2: ILine): IPoint | null {

        const a1 = line1.endPoint.y - line1.startPoint.y;
        const b1 = line1.startPoint.x - line1.endPoint.x;
        const c1 = a1 * line1.startPoint.x + b1 * line1.startPoint.y;

        const a2 = line2.endPoint.y - line2.startPoint.y;
        const b2 = line2.startPoint.x - line2.endPoint.x;
        const c2 = a2 * line2.startPoint.x + b2 * line2.startPoint.y;

        const determinant = a1 * b2 - a2 * b1;

        if (determinant === 0) { // The lines are parallel
            return null;
        } else {
            const x = (b2 * c1 - b1 * c2) / determinant;
            const y = (a1 * c2 - a2 * c1) / determinant;
            return { x: x, y: y };
        }

    }


    public static PointIsInsideCircle(point: IPoint, circleCenter: IPoint, radius: number): boolean {
        const dx = point.x - circleCenter.x;
        const dy = point.y - circleCenter.y;
        return (dx * dx + dy * dy) <= (radius * radius);
    }

    public static PointIsLeftOfLine(point: IPoint, startPoint: IPoint, endPoint: IPoint): boolean {
        const vector = VectorHelpers.FromPoints(startPoint, endPoint);
        const pointVector = { x: point.x - startPoint.x, y: point.y - startPoint.y };
        return VectorHelpers.CrossProduct(vector, pointVector) > 0;
    }

    //from GeometryHeplers.PoistIsLeftOfArc
    public static PointIsLeftOfArc(point: IPoint, arc: IArc): boolean {
        const leftOfConnectingLine: boolean = GeometryHelpers.PointIsLeftOfLine(point, arc.startPoint, arc.endPoint);
        const arcRadius = arc.radius;
        if (arc.isClockwise) {
            if (leftOfConnectingLine) {
                return VectorHelpers.GetVectorLength(VectorHelpers.FromPoints(point, arc.centerPoint)) >= arcRadius;
            } else {
                return false;
            }

        } else {
            if (leftOfConnectingLine) {
                return true;
            } else {
                return VectorHelpers.GetVectorLength(VectorHelpers.FromPoints(point, arc.centerPoint)) < arcRadius;
            }
        }
    }

    public static PointIsInsideShape(point: IPoint, shape: ISegment[]): boolean {

        if (shape.length === 1 && shape[0].type === SegmentTypeEnum.Circle) {
            const circle = shape[0] as ICircle;
            return GeometryHelpers.PointIsInsideCircle(point, circle.centerPoint, circle.radius);
        } else {

            for (const segment of shape) {
                switch (segment.type) {
                    case SegmentTypeEnum.Line:
                        const line = segment as ILine;
                        if (!GeometryHelpers.PointIsLeftOfLine(point, line.startPoint, line.endPoint)) {
                            return false;
                        }
                        break;
                    case SegmentTypeEnum.Arc:
                        const arc = segment as IArc;
                        if (!GeometryHelpers.PointIsLeftOfArc(point, arc)) {
                            return false;
                        }
                        break;


                }
            }

            return true;

        }

    }


}

